import React from 'react';
import {
  Box, Group, Select, Stack,
} from '@mantine/core';

import InspectorRow from '../../../puck/fields/InspectorRow';
import PuckColorField from '../../../puck/fields/PuckColorField';
import TextStyleToggles from '../../../puck/fields/TextStyleToggles';
import { PxSpacingInput } from '../../../puck/fields/puckPxSpacingField';
import { usePuckRichTextStyle } from '../../../context/PuckRichTextStyleContext';

/** Polices proposées pour les liens d'en-tête et de pied de page. */
export const LINK_FONT_OPTIONS = [
  { label: 'Par défaut', value: '' },
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Lato', value: 'Lato, sans-serif' },
  { label: 'Poppins', value: 'Poppins, sans-serif' },
  { label: 'Playfair Display', value: 'Playfair Display, serif' },
  { label: 'Merriweather', value: 'Merriweather, serif' },
];

/** Style de rendu (aperçu + liens) à partir de la valeur du champ. */
export function linkStyleToCss(style) {
  const s = style || {};
  return {
    fontFamily: s.fontFamily || undefined,
    fontSize: s.fontSize || undefined,
    fontWeight: s.bold ? 700 : undefined,
    fontStyle: s.italic ? 'italic' : undefined,
    textDecoration: s.underline ? 'underline' : undefined,
    color: s.color || undefined,
  };
}

/**
 * Style commun des liens : police, taille, gras/italique/souligné, couleur.
 *
 * L'en-tête et le pied de page l'écrivaient chacun de leur côté, au caractère
 * près — jusqu'aux options de police et au commentaire. Ce qui les distingue
 * vraiment leur reste : le pied de page ajoute un avertissement de contraste à
 * côté de la couleur (`colorExtra`) et son nombre de colonnes (`children`).
 *
 * NB : le titre nomme un groupe, pas un <label> (qui focus le premier input
 * au moindre clic dans le bloc) — cf. InspectorRow sans `htmlFor`.
 */
export default function LinkStyleToolbar({
  value, onChange, colorValue, colorExtra = null, children = null,
  label = 'Style des liens', fontLabel = 'Police des liens', fontOptions = LINK_FONT_OPTIONS,
}) {
  const { swatches } = usePuckRichTextStyle();
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const toggle = (key) => set({ [key]: !v[key] });

  return (
    <InspectorRow label={label} layout="stacked">
      <Stack gap="xs">
        <Group gap="xs" wrap="wrap" align="flex-end">
          <Select
            data={fontOptions}
            value={v.fontFamily || ''}
            onChange={(val) => set({ fontFamily: val || '' })}
            size="xs"
            w={140}
            aria-label={fontLabel}
            comboboxProps={{ withinPortal: true }}
          />
          <Box w={92}>
            <PxSpacingInput
              value={v.fontSize || ''}
              onChange={(val) => set({ fontSize: val })}
              placeholder="Taille"
            />
          </Box>
          <TextStyleToggles
            active={{ bold: v.bold, italic: v.italic, underline: v.underline }}
            onToggle={toggle}
          />
          <Group gap={4} align="center" wrap="nowrap">
            <PuckColorField
              label="Couleur"
              value={colorValue ?? (v.color || '')}
              onChange={(val) => set({ color: val })}
              swatches={swatches.map((s) => s.value).filter(Boolean)}
              size="xs"
              glyph="text"
            />
            {colorExtra}
          </Group>
        </Group>

        {children}
      </Stack>
    </InspectorRow>
  );
}
